import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import heroPlay from "@/assets/hero-play.jpg";

const Hero = () => {
  return (
    <section className="relative overflow-hidden bg-muted/30 py-16 lg:py-24">
      <div className="container mx-auto px-4">
        {/* Main Hero Content */}
        <div className="text-center max-w-4xl mx-auto mb-16">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-foreground mb-6 leading-tight tracking-tight">
            MSB Kids Programs: Where Kids{" "}
            <span className="text-gradient">Play, Experience</span>
            <br />
            and <span className="text-accent">Grow</span>
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
            Enriching after-school programs, clubs, holiday break camps, and summer camps designed to inspire creativity, build confidence, and create lasting memories.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/programs">
              <Button size="lg" className="font-bold rounded-full px-8 text-base gap-2">
                All programs <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="font-bold rounded-full px-8 text-base">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>

        {/* Photo Grid with Pillars */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          <PillarCard
            image={heroPlay}
            title="Play"
            alt="Kids playing outdoors and laughing together at an MSB Camps program"
            color="destructive"
            delay={0}
            priority
          />
          <PillarCard
            image="photo-1544776193-352d25ca82cd"
            title="Experience"
            alt="Children exploring hands-on activities during an enrichment program"
            color="secondary"
            delay={100}
          />
          <PillarCard
            image="photo-1588075592446-265fd1e6e76f"
            title="Grow"
            alt="Smiling kids building friendships and confidence at summer camp"
            color="accent"
            delay={200}
          />


        </div>
      </div>
    </section>
  );
};

interface PillarCardProps {
  image: string;
  title: string;
  alt: string;
  color: "destructive" | "secondary" | "accent";
  delay: number;
  priority?: boolean;
}

const PillarCard = ({ image, title, alt, color, delay, priority }: PillarCardProps) => {
  const colorClasses = {
    destructive: "bg-destructive",
    secondary: "bg-secondary text-secondary-foreground",
    accent: "bg-accent",
  };

  const textColor = color === "secondary" ? "text-secondary-foreground" : "text-primary-foreground";

  const isLocal = image.startsWith("/") || image.includes(".");
  const base = isLocal ? image : `https://images.unsplash.com/${image}`;
  const src = isLocal ? image : `${base}?w=500&h=584&fit=crop&auto=format&q=60&fm=webp`;
  const srcSet = isLocal
    ? undefined
    : `${base}?w=400&h=467&fit=crop&auto=format&q=60&fm=webp 400w, ${base}?w=500&h=584&fit=crop&auto=format&q=60&fm=webp 500w, ${base}?w=800&h=934&fit=crop&auto=format&q=60&fm=webp 800w`;



  return (
    <div
      className="group relative overflow-hidden rounded-3xl aspect-[3/4] cursor-pointer animate-fade-up"
      style={{ animationDelay: `${delay}ms` }}
    >
      <img
        src={src}
        srcSet={srcSet}
        sizes="(max-width: 768px) 100vw, 33vw"
        alt={alt}
        width={600}
        height={700}
        loading={priority ? "eager" : "lazy"}
        fetchPriority={priority ? "high" : "auto"}
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
      <div className="absolute bottom-6 left-6 right-6">
        <span className={`inline-block px-6 py-3 rounded-full font-bold text-xl ${colorClasses[color]} ${textColor} shadow-lg`}>
          {title}
        </span>
      </div>
    </div>
  );
};


export default Hero;